import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootState } from '../../store';
import apis from '../../utils/apis';
import { formatRoleNameForDisplay, formatRoleNameForBackend } from '../../utils/roleFormatter';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import SkeletonClientItem from '../Clients/SkeletonClientItem';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { PiTrashLight, PiPencilLight, PiXLight, PiCaretRightLight, PiGearLight } from 'react-icons/pi';
import * as PiIcons from 'react-icons/pi';



interface Role {
  id: string | number;
  name: string;
  description?: string;
  display_name?: string;
  permission_ids: number[];
  permission_count: number;
  is_active: boolean;
  is_custom: boolean;
  is_default: boolean;
  created_at: string;
  updated_at?: string;
  org_type?: string;
}

interface Permission {
  id: number;
  name: string;
  section_id: number;
  description?: string;
}

interface PermissionSection {
  id: number;
  name: string;
  category?: string;
  description?: string;
  permissions: Permission[];
  permission_count: number;
}

// Mapping of permission section names to Phosphor icon keys
const sectionIconMap: Record<string, string> = {
  'Dashboard': 'PiDashboard',
  'Tasks': 'PiCheckSquare',
  'Calendar': 'PiCalendar',
  'Messages': 'PiChat',
  'Clients': 'PiUsers',
  'Lawyers': 'PiUserCircle',
  'Projects': 'PiTreeStructure',
  'Matters': 'PiBriefcase',
  'Files': 'PiFiles',
  'Research': 'PiMagnifyingGlass',
  'Tables': 'PiGridFour',
  'Analytics': 'PiChartBar',
  'Data Sources': 'PiDatabase',
  'Finder': 'PiUserSwitch',
  'Templates': 'PiFileText',
  'People': 'PiUsers',
  'Departments': 'PiBuilding',
  'Teams': 'PiUsersThree',
  'Finance': 'PiCoinVertical',
};

const getIconByName = (name: string): JSX.Element => {
  const normalizedName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  const iconKey = sectionIconMap[normalizedName] || sectionIconMap[name] || 'PiGear';
  const IconComponent = (PiIcons as any)[iconKey + 'Light'] || (PiIcons as any)[iconKey];
  return IconComponent ? <IconComponent className="h-4 w-4" /> : <PiGearLight className="h-4 w-4" />;
};

const capitalizeName = (str: string): string => {
  return str.split(/[\s_-]/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

const AdminRoles: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [roles, setRoles] = useState<Role[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PermissionSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const navigationInProgressRef = React.useRef(false);
  const renderCountRef = React.useRef(0);
  const unmountedRef = React.useRef(false);

  renderCountRef.current++;
  if (renderCountRef.current % 10 === 1 && renderCountRef.current > 1) {
    console.warn('[AdminRoles] ALERT: Component has rendered', renderCountRef.current, 'times!');
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchRoles(), fetchPermissions()]);
    };
    loadData();
    return () => {
    };
  }, [userInfo]);



  const fetchRoles = async () => {
    try {
      setLoading(true);
      const startTime = Date.now();

      // Get organization type from user info or onboarding state
      // Default to 'law_firm' if not available
      const orgType = (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';

      const response = await apis.get('/admin/roles', {
        params: {
          org_type: orgType
        }
      });

      const duration = Date.now() - startTime;

      if (response.data.success) {
        const rolesData = response.data.data || [];

        setRoles(rolesData);
        setFilteredRoles(rolesData);

        if (rolesData.length === 0) {
          console.warn('No roles found. The organization may not have any roles configured yet.');
        }
      } else {
        console.error('API returned success: false', response.data);
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.error || t('Failed to fetch roles'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      console.error('Error details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message
      });
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.error || error?.message || t('Failed to fetch roles'),
        modalType: 'error',
        duration: 4000
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const orgType = (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';
      const response = await apis.get('/admin/permissions', {
        params: {
          org_type: orgType
        }
      });
      if (response.data.success) {
        setPermissions(response.data.data || []);
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.error || t('Failed to fetch permissions'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      console.error('Error fetching permissions:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.error || t('Failed to fetch permissions'),
        modalType: 'error',
        duration: 4000
      }));
    }
  };

  useEffect(() => {
    const filtered = roles.filter(
      (role) =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredRoles(filtered);
  }, [searchTerm, roles]);

  const handleSearchChange = (query: string) => {
    setSearchTerm(query);
  };

  const handleRoleClick = React.useCallback((roleId: string | number) => {
    history.push(`/admin/roles/${roleId}`);
  }, [history]);

  const roleFilters = [
    {
      id: 'Type',
      name: 'Type',
      options: [
        { value: 'custom', label: 'Custom' },
        { value: 'default', label: 'Default' },
      ],
    },
  ];

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    // Filter already applied through searchTerm
    // This is here for consistency with other pages
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
  };

  const handleCreateRoleConfirm = async (roleData: any) => {
    try {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Role created successfully'),
        modalType: 'success',
        duration: 3000
      }));
      fetchRoles();
    } catch (error) {
      console.error('Error handling role creation:', error);
    }
  };

  const handleEditRole = (role: Role) => {
    if (!role.is_custom) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Default roles cannot be edited'),
        modalType: 'warning',
        duration: 3000
      }));
      return;
    }
    const orgType = (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';
    setEditingRole(role);
    dispatch(showModal({
      showModal: true,
      type: 'adminEditRole',
      role: role,
      message: t('Edit Role'),
      orgType: orgType,
      onConfirm: handleEditRoleConfirm,
      onCancel: () => {
        setEditingRole(null);
        dispatch(hideModal());
      },
    }));
  };

  const handleEditRoleConfirm = async (roleData: any) => {
    try {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Role updated successfully'),
        modalType: 'success',
        duration: 3000
      }));
      setEditingRole(null);
      fetchRoles();
    } catch (error) {
      console.error('Error handling role update:', error);
    }
  };

  const handleDeleteRole = async (roleId: string | number) => {
    const role = roles.find((r) => r.id === roleId);

    if (!role || !role.is_custom) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Default roles cannot be deleted'),
        modalType: 'warning',
        duration: 3000
      }));
      return;
    }

    if (!window.confirm(t('Are you sure you want to delete this role?'))) {
      return;
    }

    try {
      setIsSaving(true);
      const response = await apis.delete(`/admin/roles/${roleId}`);

      if (response.data.success) {
        setRoles(roles.filter((r) => r.id !== roleId));
        setFilteredRoles(filteredRoles.filter((r) => r.id !== roleId));
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Role deleted successfully'),
          modalType: 'success',
          duration: 3000
        }));
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.error || t('Failed to delete role'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      console.error('Error deleting role:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.error || t('Failed to delete role'),
        modalType: 'error',
        duration: 4000
      }));
    } finally {
      setIsSaving(false);
    }
  };


  const columns = ['Name', 'Type', 'Permissions', 'Status', 'Created', 'Actions'];

  const getPermissionDescriptions = (permIds: number[]): string[] => {
    const descriptions: string[] = [];
    permissions.forEach((section) => {
      section.permissions.forEach((perm) => {
        if (permIds.includes(perm.id)) {
          descriptions.push(perm.description || perm.name);
        }
      });
    });
    return descriptions;
  };

  const tableData = filteredRoles.map((role) => {
    const displayName = formatRoleNameForDisplay(role.name);
    return {
      id: role.id,
      'Name': (
        <button
          onClick={() => handleRoleClick(role.id)}
          className="text-left hover:underline focus:outline-none cursor-pointer font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          {displayName}
        </button>
      ),
      'Type': (
        <Badge color={role.is_custom ? 'blue' : 'gray'} className="text-[10px]">
          {role.is_custom ? t('Custom') : t('Default')}
        </Badge>
      ),
      'Permissions': (
        <Badge color="blue" className="text-[10px]">
          {role.permission_count} {role.permission_count === 1 ? t('permission') : t('permissions')}
        </Badge>
      ),
      'Status': (
        <Badge color={role.is_active ? 'green' : 'gray'}>
          {role.is_active ? t('Active') : t('Inactive')}
        </Badge>
      ),
      'Created': (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(role.created_at).toLocaleDateString()}
        </span>
      ),
      'Actions': null,
      rawRole: role,
    };
  });

  const roleActions = [
    {
      label: t('Edit'),
      onClick: (row: any) => handleEditRole(row.rawRole),
      icon: <PiPencilLight className="h-4 w-4" />,
      disabled: (row: any) => !row.rawRole.is_custom
    },
    {
      label: t('Delete'),
      onClick: (row: any) => handleDeleteRole(row.rawRole.id),
      icon: <PiTrashLight className="h-4 w-4 text-red-600" />,
      isDestructive: true,
      disabled: (row: any) => !row.rawRole.is_custom
    },
  ];

  return (
    <div className="flex flex-col">
      <div className="px-4 py-2">
        <FilterBarWithTabs
          filters={roleFilters}
          selectedFilters={{}}
          onFilterChange={handleFilterChange}
          onSearchChange={handleSearchChange}
          onClearAllFilters={handleClearAllFilters}
          noParentPadding
        />
      </div>


      <div className="flex-1">
        {loading ? (
          <div className="px-4">
            <SkeletonClientItem viewMode="table" count={3} />
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500 dark:text-gray-400">{t('No roles found')}</p>
          </div>
        ) : (
          <div className="px-4">
            <Table
              columns={columns}
              data={tableData}
              actions={roleActions}
              onRowClick={(row) => handleRoleClick(row.id)}
              total_items={roles.length}
              total_showing_items={filteredRoles.length}
              setIsShowing={true}
              showingLabel={t('roles')}
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default AdminRoles;
